import { useEffect } from 'react';
import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { RoleGate, AdminNav, useAdminT, useCurrentRole } from '../../features/admin/index.js';
import { Avatar, cn } from '@xdocs/ui';
import { useSettingsProfile } from '../settings/useSettingsProfile.js';
import styles from './AdminLayout.module.css';

const DARK_THEME_ATTR = 'data-theme';

/**
 * Forces the admin shell into dark theme regardless of the signed-in
 * staffer's own app-wide preference (digest-design: "Admin defaults dark
 * theme"). `@xdocs/ui`'s `ThemeProvider` sets `data-theme` on
 * `document.documentElement` (`:root[data-theme='dark']` is the only
 * selector `tokens.css` matches — there is no scoped per-subtree override,
 * and adding one is outside this task's `packages/ui` ownership boundary),
 * so this reaches for the same attribute directly rather than through
 * `useTheme().setTheme('dark')` — that setter ALSO persists to
 * `localStorage` (`ThemeController.tsx`), which would silently overwrite
 * the user's own light/dark/system choice for the rest of the app. Restores
 * whatever `data-theme` was present (or its absence, meaning "system") on
 * unmount, so leaving `/admin/*` returns the shopper-facing app to the
 * staffer's actual preference.
 */
function useForceDarkTheme(): void {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute(DARK_THEME_ATTR);
    root.setAttribute(DARK_THEME_ATTR, 'dark');
    return () => {
      if (previous === null) root.removeAttribute(DARK_THEME_ATTR);
      else root.setAttribute(DARK_THEME_ATTR, previous);
    };
  }, []);
}

/**
 * Role-gated admin shell (F53-F61/F68/F71 — design/app/38-49 mockups,
 * PLAN.md ADR-2: admin is a section of `@xdocs/web`, not a Supabase app).
 * Sidebar (`AdminNav`, role-filtered) + topbar (env/role badges + staffer
 * identity, sourced from the already-fetched `useCurrentRole`/
 * `useSettingsProfile` — no new WS op) + `<Outlet/>` for the routed admin
 * page. Gated at `minimum='moderator'` — moderators reach
 * `/admin/assets` and `/admin/review-queue` (§15.2); each admin-ONLY page
 * this task owns (`overview`/`users`/`analytics`) wraps itself in a second,
 * stricter `<RoleGate minimum="admin">` so a moderator hitting those URLs
 * directly still gets the 403 state rather than an empty/broken page.
 *
 * Does NOT mount `<ImpersonationProvider>`/`<ImpersonationBanner>` — see
 * `WIRING.md`: that has to live ABOVE the router in `App.tsx` so the banner
 * survives navigating out of `/admin/**` into ordinary app routes once a
 * view-as session starts.
 */
export function AdminLayout(): ReactElement {
  const { t } = useAdminT();
  const { role } = useCurrentRole();
  const { profile } = useSettingsProfile();
  useForceDarkTheme();

  const roleLabel = role === 'admin' ? t('admin:shell.roleAdmin') : role === 'moderator' ? t('admin:shell.roleModerator') : null;
  const identityName = profile?.displayName ?? profile?.email ?? '';

  return (
    <RoleGate minimum="moderator">
      <div className={cn(styles['shell'])}>
        <aside className={cn(styles['sidebar'])} aria-label={t('admin:shell.title')}>
          <div className={cn(styles['brandRow'])}>
            <div className={cn(styles['brand'])}>{t('admin:shell.title')}</div>
            <span className={cn(styles['brandBadge'])}>{t('admin:shell.brandBadge')}</span>
          </div>
          <div className={cn(styles['navGroup'])}>
            <div className={cn(styles['navGroupLabel'])}>{t('admin:shell.navGroup')}</div>
            <AdminNav role={role} />
          </div>
          <NavLink to="/" className={cn(styles['backLink'])}>
            <ArrowLeft size={14} aria-hidden="true" />
            {t('admin:shell.backToApp')}
          </NavLink>
        </aside>
        <div className={cn(styles['main'])}>
          {/* Topbar (design/app/38-49): env + role badges, staffer identity.
              NO theme toggle (admin is forced dark by design — see
              `useForceDarkTheme` above — a toggle here would either be a
              dead control or silently fight that decision) and no global
              "jump to user or asset" search (the mockup's box has no wired
              backend op in this task's frozen `wsOp` enum — shipping an
              input that can't actually search would be a dead control
              too; flagged as an open issue for whichever workstream adds
              that lookup). */}
          <header className={cn(styles['topbar'])}>
            <div className={cn(styles['topbarSpacer'])} />
            <span className={cn(styles['envBadge'])} data-env={import.meta.env.PROD ? 'prod' : 'dev'}>
              {import.meta.env.PROD ? t('admin:shell.envProd') : t('admin:shell.envDev')}
            </span>
            {roleLabel && <span className={cn(styles['roleBadge'])}>{roleLabel}</span>}
            <div className={cn(styles['identity'])}>
              <Avatar name={identityName} size={34} loading={!profile} />
              {profile && (
                <div className={cn(styles['identityText'])}>
                  <div className={cn(styles['identityName'])}>{identityName}</div>
                  <div className={cn(styles['identityEmail'])}>{profile.email}</div>
                </div>
              )}
            </div>
          </header>
          <main id="admin-main-content" className={cn(styles['content'])}>
            <Outlet />
          </main>
        </div>
      </div>
    </RoleGate>
  );
}
